import React, { useState, useRef, useCallback } from 'react';
import { GoogleMap, Marker, Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import './AddClientModal.css';
import { GOOGLE_MAPS_ID, GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_VERSION } from '../../config/googleMaps';
import Loader from '../../mainframes/Loader';

const libraries = ['places', 'geometry', 'drawing'];

const mapContainerStyle = { 
  width: '100%', 
  height: '350px', 
  borderRadius: '8px', 
  marginTop: '10px', 
  border: '1px solid #dee2e6' 
};
const defaultCenter = { lat: 20, lng: 0 };

const AddClientModal = ({ onClose, onSubmit }) => {
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });

  const [formData, setFormData] = useState({
    entityType: '',
    clientName: '',
    contactPersonName: '',
    contactPersonEmail: '',
    contactPersonPhone: '',
    vat: '',
    clientAddress: '',
    clientLatitude: null,
    clientLongitude: null
  });

  const [map, setMap] = useState(null);
  const autocompleteRef = useRef(null);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const address = place.formatted_address;

        setFormData(prev => ({ 
          ...prev, 
          clientAddress: address, 
          clientLatitude: lat, 
          clientLongitude: lng 
        }));
        
        if (map) {
          map.panTo({ lat, lng });
          map.setZoom(15);
        }
      }
    }
  };

  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setFormData(prev => ({ ...prev, clientLatitude: lat, clientLongitude: lng }));
  };

  const onLoadMap = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  if (!isLoaded) return <Loader label="Loading Map..." minHeight="100vh" background="rgba(15, 23, 42, 0.45)" />;

  return (
    <div className="acm-modal-overlay">
      <div className="acm-modal-content">
        <div className="acm-modal-header">
          <h3>Add Client</h3>
          <button className="acm-close-btn" onClick={onClose}>&times;</button>
        </div>
        
        <div className="acm-modal-body">
          <div className="acm-form-grid-3">
            <div className="acm-form-group">
              <label>Entity Type</label>
              <select name="entityType" className="acm-form-control" onChange={handleChange}>
                <option value="">Select</option>
                <option value="Individual">Individual</option>
                <option value="Company">Company</option>
                <option value="Partnership">Partnership</option>
                <option value="LLP">LLP</option>
              </select>
            </div>
            <div className="acm-form-group">
              <label>Client Name *</label>
              <input type="text" name="clientName" className="acm-form-control" onChange={handleChange} />
            </div>
            <div className="acm-form-group">
              <label>Contact Person Name</label>
              <input type="text" name="contactPersonName" className="acm-form-control" onChange={handleChange} />
            </div>
          </div>

          <div className="acm-form-grid-3">
            <div className="acm-form-group">
              <label>Contact Email</label>
              <input type="email" name="contactPersonEmail" className="acm-form-control" onChange={handleChange} />
            </div>
            <div className="acm-form-group">
              <label>Contact Phone *</label>
              <input type="text" name="contactPersonPhone" className="acm-form-control" onChange={handleChange} />
            </div>
            <div className="acm-form-group">
              <label>VAT</label>
              <input type="text" name="vat" className="acm-form-control" onChange={handleChange} />
            </div>
          </div>

          <div className="acm-form-grid-1">
            <div className="acm-form-group">
              <label>Client Address</label>
              <Autocomplete
                onLoad={ref => autocompleteRef.current = ref}
                onPlaceChanged={onPlaceChanged}
              >
                <input 
                  type="text" 
                  name="clientAddress" 
                  value={formData.clientAddress}
                  placeholder="Type to search location..." 
                  className="acm-form-control" 
                  onChange={handleChange}
                />
              </Autocomplete>
            </div>
          </div>
          
          <div className="acm-form-group">
            <label>Pin Location on Map</label>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={formData.clientLatitude ? { lat: formData.clientLatitude, lng: formData.clientLongitude } : defaultCenter}
              zoom={formData.clientLatitude ? 15 : 2}
              onLoad={onLoadMap}
              onClick={handleMapClick}
            >
              {formData.clientLatitude && formData.clientLongitude && (
                <Marker 
                  position={{ lat: formData.clientLatitude, lng: formData.clientLongitude }} 
                  draggable={true}
                  onDragEnd={handleMapClick} 
                />
              )}
            </GoogleMap>
          </div>

        </div>
        <div className="acm-modal-footer">
          <button className="acm-btn acm-btn-submit" onClick={() => onSubmit(formData)}>SUBMIT</button>
          <button className="acm-btn acm-btn-cancel" onClick={onClose}>CANCEL</button>
        </div>
      </div>
    </div>
  );
};

export default AddClientModal;
